import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, ShieldCheck, Globe2, Lock, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { BRAND } from './ImpactDashboardTab';

const CATEGORIES = [
  { key: 'output', label: 'Output', hint: 'What DCELC delivered' },
  { key: 'participation', label: 'Participation', hint: 'Who engaged and how often' },
  { key: 'outcome', label: 'Outcome', hint: 'Documented changes in knowledge, practice, leadership or systems' },
  { key: 'contribution', label: 'Contribution', hint: "DCELC's evidenced role in a broader change" },
];
const TYPES = ['highlight', 'milestone', 'testimonial', 'case_study', 'policy_development', 'photo', 'document', 'other'];
const EMPTY = { title: '', description: '', program_key: '', category: 'output', type: 'highlight', evidence_date: '', period_label: '', source: '', evidence_url: '', attribution: '' };

export default function ImpactOutcomes({ API_URL, getAuthHeader, refreshKey }) {
  const [items, setItems] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [catFilter, setCatFilter] = useState('');
  const [progFilter, setProgFilter] = useState('');
  const [form, setForm] = useState(null);

  const fetchAll = useCallback(async () => {
    const qs = new URLSearchParams();
    if (catFilter) qs.set('category', catFilter);
    if (progFilter) qs.set('program', progFilter);
    try {
      const [er, pr] = await Promise.all([
        fetch(`${API_URL}/api/impact/evidence?${qs}`, { headers: getAuthHeader() }),
        fetch(`${API_URL}/api/impact/programs`, { headers: getAuthHeader() }),
      ]);
      if (er.ok) setItems(await er.json());
      if (pr.ok) setPrograms(await pr.json());
    } catch { /* noop */ }
  }, [API_URL, getAuthHeader, catFilter, progFilter]);

  useEffect(() => { fetchAll(); }, [fetchAll, refreshKey]);

  const save = async () => {
    if (!form.title.trim() || !form.program_key) return toast.error('Title and program are required');
    const isEdit = Boolean(form.id);
    const url = isEdit ? `${API_URL}/api/impact/evidence/${form.id}` : `${API_URL}/api/impact/evidence`;
    const r = await fetch(url, {
      method: isEdit ? 'PATCH' : 'POST',
      headers: { ...getAuthHeader(), 'Content-Type': 'application/json' },
      body: JSON.stringify(form)
    });
    if (r.ok) { toast.success(isEdit ? 'Evidence updated' : 'Evidence added'); setForm(null); fetchAll(); }
    else { const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Save failed'); }
  };

  const patch = async (id, body, msg) => {
    const r = await fetch(`${API_URL}/api/impact/evidence/${id}`, {
      method: 'PATCH', headers: { ...getAuthHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify(body)
    });
    if (r.ok) { toast.success(msg); fetchAll(); }
    else { const d = await r.json().catch(() => ({})); toast.error(d.detail || 'Update failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this evidence record? This is logged in the audit trail.')) return;
    const r = await fetch(`${API_URL}/api/impact/evidence/${id}`, { method: 'DELETE', headers: getAuthHeader() });
    if (r.ok) { toast.success('Evidence deleted'); fetchAll(); }
  };

  const progName = (key) => programs.find(p => p.key === key)?.name || key;

  return (
    <div className="space-y-4" data-testid="impact-outcomes">
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setCatFilter('')} className="px-3 py-1.5 rounded-lg text-xs font-bold"
          style={!catFilter ? { background: BRAND.navy, color: '#fff' } : { background: '#fff', color: BRAND.navy, border: '1px solid #e2e8f0' }}>All</button>
        {CATEGORIES.map(c => (
          <button key={c.key} onClick={() => setCatFilter(c.key)} title={c.hint} data-testid={`impact-evidence-cat-${c.key}`}
            className="px-3 py-1.5 rounded-lg text-xs font-bold"
            style={catFilter === c.key ? { background: BRAND.navy, color: '#fff' } : { background: '#fff', color: BRAND.navy, border: '1px solid #e2e8f0' }}>
            {c.label}
          </button>
        ))}
        <select value={progFilter} onChange={e => setProgFilter(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-1.5 text-xs bg-white">
          <option value="">All programs</option>
          {programs.map(p => <option key={p.key} value={p.key}>{p.name}</option>)}
        </select>
        <button onClick={() => setForm({ ...EMPTY })} data-testid="impact-add-evidence-btn"
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white ml-auto" style={{ background: BRAND.teal }}>
          <Plus className="w-4 h-4" /> Add evidence
        </button>
      </div>
      <p className="text-xs text-slate-400">Outputs (what we delivered) · Participation (who engaged) · Outcomes (documented changes) · Contribution (our evidenced role). Only verified items can be approved for public use; internal observations stay separate from approved public statements.</p>

      {form && (
        <div className="bg-white rounded-xl border border-slate-200 p-5 grid md:grid-cols-2 gap-3" data-testid="impact-evidence-form">
          <input placeholder="Title *" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            data-testid="impact-evidence-title" className="border border-slate-300 rounded-lg px-3 py-2 text-sm md:col-span-2" />
          <textarea placeholder="Description — what happened, who was involved, what changed" value={form.description} rows={3}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="border border-slate-300 rounded-lg px-3 py-2 text-sm md:col-span-2" />
          <select value={form.program_key} onChange={e => setForm(f => ({ ...f, program_key: e.target.value }))}
            data-testid="impact-evidence-program" className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
            <option value="">Program *</option>
            {programs.map(p => <option key={p.key} value={p.key}>{p.name}</option>)}
          </select>
          <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
            data-testid="impact-evidence-category" className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white">
            {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label} — {c.hint}</option>)}
          </select>
          <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white capitalize">
            {TYPES.map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
          </select>
          <input type="date" value={form.evidence_date || ''} onChange={e => setForm(f => ({ ...f, evidence_date: e.target.value }))} className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          <input placeholder="Reporting period label (e.g. FY2026)" value={form.period_label || ''} onChange={e => setForm(f => ({ ...f, period_label: e.target.value }))} className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          <input placeholder="Source (who documented this)" value={form.source || ''} onChange={e => setForm(f => ({ ...f, source: e.target.value }))} className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          <input placeholder="Evidence link (URL)" value={form.evidence_url || ''} onChange={e => setForm(f => ({ ...f, evidence_url: e.target.value }))} className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          <input placeholder="Attribution / permitted use notes" value={form.attribution || ''} onChange={e => setForm(f => ({ ...f, attribution: e.target.value }))} className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
          <div className="flex gap-2 md:col-span-2">
            <button onClick={save} data-testid="impact-evidence-save" className="px-4 py-2 rounded-lg text-sm font-semibold text-white" style={{ background: BRAND.navy }}>{form.id ? 'Save changes' : 'Add evidence'}</button>
            <button onClick={() => setForm(null)} className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-500 border border-slate-200">Cancel</button>
          </div>
        </div>
      )}

      {items.length === 0 && !form && (
        <div className="bg-white rounded-xl border border-slate-200 p-10 text-center text-slate-400 text-sm">
          No evidence records yet. Capture highlights, milestones, testimonials and documented outcomes as they happen — your year-end report will thank you.
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {items.map(ev => (
          <div key={ev.id} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`impact-evidence-${ev.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded" style={{ background: BRAND.navy, color: '#fff' }}>{CATEGORIES.find(c => c.key === ev.category)?.label || ev.category}</span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-slate-100 text-slate-600 capitalize">{(ev.type || '').replace('_', ' ')}</span>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded" style={{ background: '#e6f7f6', color: BRAND.teal }}>{progName(ev.program_key)}</span>
                </div>
                <h3 className="font-bold text-sm" style={{ color: BRAND.navy, fontFamily: 'Roboto, Arial, sans-serif' }}>{ev.title}</h3>
              </div>
              <div className="flex gap-1 shrink-0">
                <button onClick={() => setForm({ ...ev })} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                <button onClick={() => remove(ev.id)} data-testid={`impact-evidence-delete-${ev.id}`} className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            {ev.description && <p className="text-xs text-slate-500 mt-2 leading-relaxed">{ev.description}</p>}
            <div className="text-[11px] text-slate-400 mt-2 space-x-2">
              {ev.evidence_date && <span>{ev.evidence_date}</span>}
              {ev.period_label && <span>· {ev.period_label}</span>}
              {ev.source && <span>· Source: {ev.source}</span>}
              {ev.evidence_url && <a href={ev.evidence_url} target="_blank" rel="noopener noreferrer" className="underline" style={{ color: BRAND.teal }}>evidence link</a>}
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              <button onClick={() => patch(ev.id, { verification_status: ev.verification_status === 'verified' ? 'unverified' : 'verified' },
                ev.verification_status === 'verified' ? 'Marked unverified' : 'Marked verified')}
                data-testid={`impact-evidence-verify-${ev.id}`}
                className="flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full border"
                style={ev.verification_status === 'verified' ? { background: '#f0f9e8', color: '#4d7c0f', borderColor: BRAND.lime } : { color: '#927320', background: '#fdf6e3', borderColor: BRAND.gold }}>
                <ShieldCheck className="w-3 h-3" /> {ev.verification_status === 'verified' ? 'Verified' : 'Unverified — click to verify'}
              </button>
              <button onClick={() => patch(ev.id, { publication_status: ev.publication_status === 'approved_public' ? 'internal' : 'approved_public' },
                ev.publication_status === 'approved_public' ? 'Moved to internal only' : 'Approved for public use')}
                data-testid={`impact-evidence-publish-${ev.id}`}
                className="flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full border border-slate-200"
                style={ev.publication_status === 'approved_public' ? { background: '#e6f7f6', color: BRAND.teal } : { color: '#64748b' }}>
                {ev.publication_status === 'approved_public' ? <Globe2 className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                {ev.publication_status === 'approved_public' ? 'Approved for public use' : 'Internal only'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
